// Rule Builder — the rules/milestones/tiers/pools editor that lives in the
// Rules tab of /admin/cards/:id/rules.
//
// Phase 23.E.6 refactor (May 2026):
//   • The previous monolithic per-rule expanded block has been split:
//     the LIST ROW (this file) is now a compact one-liner with inline
//     quick edits (priority, exclude, duplicate, delete, reorder, edit).
//     The full FORM lives in `RuleEditorDrawer` — opens as a Sheet on
//     desktop, full-screen on mobile, with a non-gated 5-step stepper.
//
//   • Helpers (defaults, ChipList, ConfidenceBadge, cap/pool helpers)
//     moved to `ruleBuilderShared.js` so the drawer can import them
//     without a circular dependency.
//
//   • Milestones / Tiers / Pool Capping sub-tabs are unchanged — those
//     forms are short enough (4–6 fields) that inline editing still wins.
//
// Stable test IDs preserved:
//   • `admin-rule-builder-block-${rule.id}`  — now on the LIST ROW
//     (referenced by ConflictPanel's "jump to rule" handler)
//   • `admin-rule-builder-{label|priority|exclude|...}-${rule.id}`
//     — preserved on the form fields inside the drawer
import { useMemo, useState } from 'react';
import {
  ArrowUp, ArrowDown, Trash2, Plus, AlertCircle, Layers, Lock, Eye,
  Coins, TrendingUp, Copy, Pencil, MoreVertical, Settings2,
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { RuleEditorDrawer } from '@/components/admin/RuleEditorDrawer';
import {
  ChipList, ConfidenceBadge, defaultProvenance,
  CONFIDENCE_LEVELS, SOURCE_TYPES, VERIFICATION_STATUSES,
  PERIODS,
  emptyRule, emptyMilestone, emptyCapPool, emptyTier,
  genRuleId,
  poolDimension, poolLimit, formatPoolLimit,
} from '@/components/admin/ruleBuilderShared';

// Re-export `ConfidenceBadge` so existing import paths
// (`from '@/components/admin/RuleBuilder'`) in RuleSimulator and other
// callers keep working. New code should import from ruleBuilderShared.
export { ConfidenceBadge, CONFIDENCE_LEVELS, SOURCE_TYPES, VERIFICATION_STATUSES };

// ─── Specificity badge — same scoring as the engine (mirrors phase23 weights)
function ruleSpecificity(rule) {
  const m = rule?.match || {};
  return (
    (m.mcc || []).length * 0.05
    + (m.merchants || []).length * 0.07
    + (m.categories || []).length * 0.03
    + (m.contexts || []).filter((c) => c.required !== false).length * 0.10
  );
}
function specificityMeta(rule) {
  const s = ruleSpecificity(rule);
  if (s < 0.10) return { label: 'Low',    cls: 'bg-amber-500/10 text-amber-600 border-amber-500/30',  score: s };
  if (s < 0.30) return { label: 'Medium', cls: 'bg-primary/10 text-primary border-primary/30',         score: s };
  return                { label: 'High',   cls: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/30', score: s };
}


// ──────────────────────────────────────────────────────────────────────
// Compact list row — replaces the old monolithic RuleBlock. Quick edits
// live here; clicking Edit opens the drawer for full editing.
// ──────────────────────────────────────────────────────────────────────
const RuleListRow = ({ rule, index, total, locked, onMove, onEdit, onDuplicate, onRemove, onQuickPatch }) => {
  const isExclude = !!rule.exclude;
  const provenance = rule.provenance || defaultProvenance();
  const spec = specificityMeta(rule);
  const contextCount = (rule.match?.contexts || []).length;
  const summary = useMemo(() => oneLineRuleSummary(rule), [JSON.stringify(rule || {})]);
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <Card
      className={cn(
        'p-2.5 relative transition-colors',
        isExclude ? 'border-rose-500/40 bg-rose-500/5' : 'border-border hover:border-primary/30',
      )}
      data-testid={`admin-rule-builder-block-${rule.id}`}
    >
      <div className="flex items-stretch gap-2">
        {/* Reorder + index gutter */}
        <div className="flex flex-col items-center gap-0.5 pt-0.5 shrink-0">
          <div className="text-[9px] font-mono-num text-muted-foreground w-5 text-center">{index + 1}</div>
          <button onClick={() => onMove(index, -1)} disabled={locked || index === 0} className="h-5 w-5 rounded hover:bg-muted/40 grid place-items-center disabled:opacity-30" data-testid={`admin-rule-builder-move-up-${rule.id}`} aria-label="Move up">
            <ArrowUp className="h-3 w-3" />
          </button>
          <button onClick={() => onMove(index, 1)} disabled={locked || index === total - 1} className="h-5 w-5 rounded hover:bg-muted/40 grid place-items-center disabled:opacity-30" data-testid={`admin-rule-builder-move-down-${rule.id}`} aria-label="Move down">
            <ArrowDown className="h-3 w-3" />
          </button>
        </div>

        {/* Main content (clickable to open editor) */}
        <button
          type="button"
          onClick={onEdit}
          className="flex-1 min-w-0 text-left rounded hover:bg-muted/30 focus-visible:bg-muted/30 focus-visible:outline-none transition-colors px-2 py-1 -ml-1"
          data-testid={`admin-rule-builder-edit-${rule.id}`}
          aria-label={`Edit ${rule.label}`}
        >
          <div className="flex items-center gap-1.5 flex-wrap min-w-0">
            <div className="font-medium text-[13px] truncate min-w-0 max-w-full sm:max-w-[280px]">
              {rule.label || <span className="italic text-muted-foreground">(unlabeled rule)</span>}
            </div>
            <ConfidenceBadge provenance={provenance} />
            <Badge variant="outline" className={cn('text-[10px] gap-1 uppercase tracking-wide px-1.5', spec.cls)} title={`Specificity ${spec.score.toFixed(2)}`}>
              {spec.label}
            </Badge>
            {contextCount > 0 && (
              <Badge variant="outline" className="text-[10px] gap-1 px-1.5 border-primary/30 bg-primary/5 text-primary" title="Has transaction-context conditions">
                <Settings2 className="h-2.5 w-2.5" /> {contextCount} ctx
              </Badge>
            )}
            {isExclude && <Badge variant="outline" className="text-[10px] gap-1 px-1.5 border-rose-500/40 bg-rose-500/10 text-rose-600"><Lock className="h-2.5 w-2.5" /> Exclude</Badge>}
          </div>
          <div className="text-[11px] text-muted-foreground mt-0.5 truncate">{summary}</div>
        </button>

        {/* Quick-edit cluster (right side) */}
        <div className="flex items-center gap-1 shrink-0">
          {/* Priority — inline editable */}
          <div className="hidden sm:flex flex-col items-end mr-1">
            <Label className="text-[9px] uppercase tracking-wider text-muted-foreground">Pri</Label>
            <Input
              type="number"
              value={rule.priority ?? 10}
              onChange={(e) => onQuickPatch({ priority: Number(e.target.value) || 0 })}
              disabled={locked}
              className="h-7 w-14 font-mono-num text-xs px-1.5"
              data-testid={`admin-rule-builder-priority-${rule.id}`}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          {/* Edit (primary action) */}
          <Button size="sm" variant="outline" onClick={onEdit} className="h-7 gap-1 text-[11px]" data-testid={`admin-rule-builder-edit-btn-${rule.id}`}>
            <Pencil className="h-3 w-3" /> <span className="hidden sm:inline">Edit</span>
          </Button>
          {/* Overflow */}
          <Popover open={menuOpen} onOpenChange={setMenuOpen}>
            <PopoverTrigger asChild>
              <Button size="icon" variant="ghost" disabled={locked} className="h-7 w-7 text-muted-foreground" data-testid={`admin-rule-builder-overflow-${rule.id}`} aria-label="More actions">
                <MoreVertical className="h-3.5 w-3.5" />
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-[200px] p-1">
              <button
                type="button"
                onClick={() => { setMenuOpen(false); onQuickPatch({ exclude: !isExclude }); }}
                disabled={locked}
                className="w-full text-left flex items-center gap-2 px-2 py-1.5 rounded hover:bg-muted/50 text-[12px] disabled:opacity-40"
                data-testid={`admin-rule-builder-exclude-${rule.id}`}
              >
                <Lock className={cn('h-3 w-3', isExclude && 'text-rose-500')} />
                {isExclude ? 'Un-exclude (allow earning)' : 'Set as Exclude (block earn)'}
              </button>
              <button
                type="button"
                onClick={() => { setMenuOpen(false); onDuplicate(); }}
                disabled={locked}
                className="w-full text-left flex items-center gap-2 px-2 py-1.5 rounded hover:bg-muted/50 text-[12px] disabled:opacity-40"
                data-testid={`admin-rule-builder-duplicate-${rule.id}`}
              >
                <Copy className="h-3 w-3" /> Duplicate
              </button>
              <button
                type="button"
                onClick={() => { if (window.confirm(`Delete rule "${rule.label}"?`)) { setMenuOpen(false); onRemove(); } }}
                disabled={locked}
                className="w-full text-left flex items-center gap-2 px-2 py-1.5 rounded hover:bg-rose-500/10 text-[12px] text-rose-500 disabled:opacity-40"
                data-testid={`admin-rule-builder-remove-${rule.id}`}
              >
                <Trash2 className="h-3 w-3" /> Delete rule
              </button>
            </PopoverContent>
          </Popover>
        </div>
      </div>
    </Card>
  );
};


// Compact one-line summary used on the list row.
function oneLineRuleSummary(rule) {
  const parts = [];
  const m = rule?.match || {};
  if ((m.categories || []).length) parts.push(`Cat: ${m.categories.join('|')}`);
  if ((m.merchants  || []).length) parts.push(`Merch: ${m.merchants.slice(0, 3).join('|')}${m.merchants.length > 3 ? '…' : ''}`);
  if ((m.mcc || []).length)        parts.push(`MCC: ${m.mcc.slice(0, 3).join('|')}${m.mcc.length > 3 ? '…' : ''}`);
  if (m.mode && m.mode !== 'any')  parts.push(m.mode);
  if (m.international === true)    parts.push('Intl');
  if (m.international === false)   parts.push('Domestic');
  for (const c of (m.contexts || [])) {
    const op = c.operator === 'not_in' ? '∉' : '∈';
    const k = c.context_type === 'custom' ? c.custom_key : c.context_type;
    const vs = (c.values || []).slice(0, 2).join('|');
    parts.push(`${k} ${op} {${vs}${(c.values || []).length > 2 ? '…' : ''}}`);
  }
  const matchStr = parts.length ? parts.join(' · ') : 'no criteria';

  let earnStr = '';
  if (rule.exclude) earnStr = 'EXCLUDE';
  else if (rule.earn?.type === 'percent_cashback') earnStr = `${rule.earn?.rate || 0}% cashback`;
  else earnStr = `${rule.earn?.rate || 0} ${rule.earn?.currency || 'RP'}/₹${rule.earn?.per || 0}`;

  let capStr = '';
  if (rule.cap?.pool_id) capStr = ' · shared pool';
  else if (rule.cap) {
    const k = rule.cap.amount_inr != null ? 'amount_inr' : rule.cap.reward_inr != null ? 'reward_inr' : 'reward_points';
    const v = rule.cap[k];
    if (v != null) capStr = ` · cap ${Number(v).toLocaleString('en-IN')}${k === 'reward_points' ? 'p' : '₹'}/${rule.cap.period || 'mo'}`;
  }

  return `${matchStr}  ⇒  ${earnStr}${capStr}`;
}


// ──────────────────────────────────────────────────────────────────────
// Milestone block — unchanged from previous Rule Builder.
// ──────────────────────────────────────────────────────────────────────
const MilestoneBlock = ({ m, locked, onChange, onRemove }) => {
  const set = (p) => onChange({ ...m, ...p });
  return (
    <div className="rounded-md border border-border bg-[hsl(var(--surface-2))] px-2 py-2 space-y-1.5" data-testid={`admin-milestone-block-${m.id}`}>
      <div className="flex items-center gap-1.5">
        <Input value={m.name} onChange={(e) => set({ name: e.target.value })} disabled={locked} placeholder="Milestone name (e.g. Annual ₹12L spend bonus)" className="h-7 text-xs flex-1" data-testid={`admin-milestone-name-${m.id}`} />
        <Button variant="ghost" size="icon" disabled={locked} className="h-7 w-7 shrink-0 text-muted-foreground hover:text-rose-500" onClick={onRemove} data-testid={`admin-milestone-remove-${m.id}`}>
          <Trash2 className="h-3 w-3" />
        </Button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-[110px_1fr_100px_110px] gap-1.5">
        <Input type="number" inputMode="numeric" value={m.target_inr} onChange={(e) => set({ target_inr: Number(e.target.value) || 0 })} disabled={locked} placeholder="Target ₹" className="h-7 font-mono-num text-xs" data-testid={`admin-milestone-target-${m.id}`} />
        <Input value={m.reward} onChange={(e) => set({ reward: e.target.value })} disabled={locked} placeholder="Reward (e.g. 10,000 RP bonus)" className="h-7 text-xs" data-testid={`admin-milestone-reward-${m.id}`} />
        <Input type="number" inputMode="numeric" value={m.reward_value_inr} onChange={(e) => set({ reward_value_inr: Number(e.target.value) || 0 })} disabled={locked} placeholder="₹ value" className="h-7 font-mono-num text-xs" data-testid={`admin-milestone-value-${m.id}`} />
        <Select value={m.period || 'anniversary'} onValueChange={(v) => set({ period: v })} disabled={locked}>
          <SelectTrigger className="h-7 text-xs" data-testid={`admin-milestone-period-${m.id}`}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="anniversary">Anniversary</SelectItem>
            <SelectItem value="yearly">Yearly</SelectItem>
            <SelectItem value="lifetime">Lifetime</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};


// ──────────────────────────────────────────────────────────────────────
// CapPool block — unchanged.
// ──────────────────────────────────────────────────────────────────────
const CapPoolBlock = ({ pool, locked, onChange, onRemove, usageCount, allRules }) => {
  const set = (patch) => onChange({ ...pool, ...patch });
  const dim = poolDimension(pool);
  const value = poolLimit(pool);
  const spilloverOn = (pool.spillover_mode || 'block') === 'fallback_rule';
  const candidates = (allRules || []).filter((r) => r && r.id && !r.exclude && r.earn);
  return (
    <Card className="p-3 border-amber-500/30 bg-amber-500/5" data-testid={`admin-cap-pool-block-${pool.id}`}>
      <div className="flex items-center gap-2 mb-2.5 flex-wrap">
        <Coins className="h-3.5 w-3.5 text-amber-500 shrink-0" />
        <Input value={pool.label || ''} onChange={(e) => set({ label: e.target.value })} disabled={locked} placeholder="Pool label (e.g. TravelOne portal accelerator)" className="h-8 text-sm font-medium flex-1 min-w-[180px]" data-testid={`admin-cap-pool-label-${pool.id}`} />
        {typeof usageCount === 'number' && (
          <Badge variant="outline" className="text-[10px] gap-1 px-1.5" data-testid={`admin-cap-pool-usage-${pool.id}`}>
            {usageCount} rule{usageCount === 1 ? '' : 's'}
          </Badge>
        )}
        <Button size="icon" variant="ghost" disabled={locked} className="h-7 w-7 text-muted-foreground hover:text-rose-500" onClick={onRemove} data-testid={`admin-cap-pool-remove-${pool.id}`}>
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-[1.1fr_1fr_0.9fr] gap-2">
        <div>
          <Label className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Cap on</Label>
          <Select value={dim} onValueChange={(v) => {
            const patch = { amount_inr: null, reward_inr: null, reward_points: null };
            patch[v] = value != null ? value : (v === 'reward_points' ? 18000 : 100000);
            set(patch);
          }} disabled={locked}>
            <SelectTrigger className="mt-1 h-8 text-xs" data-testid={`admin-cap-pool-dimension-${pool.id}`}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="amount_inr">Spend (₹)</SelectItem>
              <SelectItem value="reward_inr">Reward (₹)</SelectItem>
              <SelectItem value="reward_points">Reward (points)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Limit</Label>
          <Input type="number" inputMode="numeric" value={value ?? ''} onChange={(e) => {
            const v = e.target.value === '' ? null : Number(e.target.value);
            set({ [dim]: v });
          }} disabled={locked} className="mt-1 h-8 font-mono-num text-xs" data-testid={`admin-cap-pool-limit-${pool.id}`} placeholder={dim === 'reward_points' ? '18000' : '100000'} />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Per</Label>
          <Select value={pool.period || 'monthly'} onValueChange={(v) => set({ period: v })} disabled={locked}>
            <SelectTrigger className="mt-1 h-8 text-xs" data-testid={`admin-cap-pool-period-${pool.id}`}><SelectValue /></SelectTrigger>
            <SelectContent>{PERIODS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <label className="flex items-start gap-2 mt-2 cursor-pointer select-none" data-testid={`admin-cap-pool-spillover-${pool.id}`}>
        <Switch checked={spilloverOn} disabled={locked} onCheckedChange={(v) => set({ spillover_mode: v ? 'fallback_rule' : 'block' })} />
        <span className="text-[11px] leading-tight">
          <span className="font-medium">Spillover when pool is exhausted</span>
          <span className="block text-[10px] text-muted-foreground mt-0.5">Over-pool spend earns at the fallback rule below. Off = no reward on the excess.</span>
        </span>
      </label>
      {spilloverOn && (
        <div className="mt-2" data-testid={`admin-cap-pool-fallback-row-${pool.id}`}>
          <Label className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Pool-wide fallback (default for all rules in this pool)</Label>
          <Select value={pool.fallback_rule_id || '__auto__'} onValueChange={(v) => set({ fallback_rule_id: v === '__auto__' ? null : v })} disabled={locked}>
            <SelectTrigger className="mt-1 h-8 text-xs" data-testid={`admin-cap-pool-fallback-${pool.id}`}>
              <SelectValue placeholder="Auto · next-priority matching rule" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__auto__" className="text-xs">Auto · next-priority matching rule</SelectItem>
              {candidates.length > 0 && (
                <>
                  <div className="px-2 pt-2 pb-1 text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Or pick explicitly:</div>
                  {candidates.map((r) => (
                    <SelectItem key={r.id} value={r.id} className="text-xs">{r.label || r.id}</SelectItem>
                  ))}
                </>
              )}
            </SelectContent>
          </Select>
          <p className="mt-1.5 text-[10px] text-muted-foreground leading-snug">
            Rules joining this pool inherit this fallback by default; a rule can still override it in its own Spillover section.
          </p>
        </div>
      )}
      <Textarea value={pool.notes || ''} onChange={(e) => set({ notes: e.target.value })} rows={2} placeholder="Notes — describe which categories drain this pool, T&C reference, observed quirks…" className="mt-2 text-[11px]" disabled={locked} data-testid={`admin-cap-pool-notes-${pool.id}`} />
    </Card>
  );
};


// ──────────────────────────────────────────────────────────────────────
// Tier block — unchanged.
// ──────────────────────────────────────────────────────────────────────
const TierBlock = ({ tier, locked, onChange, onRemove }) => {
  const set = (patch) => onChange({ ...tier, ...patch });
  return (
    <Card className="p-3 border-primary/30 bg-primary/5" data-testid={`admin-tier-block-${tier.id}`}>
      <div className="flex items-center gap-2 mb-2.5 flex-wrap">
        <TrendingUp className="h-3.5 w-3.5 text-primary shrink-0" />
        <Input value={tier.name || ''} onChange={(e) => set({ name: e.target.value })} disabled={locked} placeholder="Tier name (e.g. Burgundy Private, Atlas Gold)" className="h-8 text-sm font-medium flex-1 min-w-[180px]" data-testid={`admin-tier-name-${tier.id}`} />
        <Button size="icon" variant="ghost" disabled={locked} className="h-7 w-7 text-muted-foreground hover:text-rose-500" onClick={onRemove} data-testid={`admin-tier-remove-${tier.id}`}>
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <div>
          <Label className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Threshold (₹)</Label>
          <Input type="number" inputMode="numeric" value={tier.threshold_inr ?? 0} onChange={(e) => set({ threshold_inr: Number(e.target.value) || 0 })} disabled={locked} className="mt-1 h-8 font-mono-num text-xs" data-testid={`admin-tier-threshold-${tier.id}`} />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Rate multiplier</Label>
          <Input type="number" step="0.1" value={tier.rate_multiplier ?? 1} onChange={(e) => set({ rate_multiplier: Number(e.target.value) || 1 })} disabled={locked} className="mt-1 h-8 font-mono-num text-xs" data-testid={`admin-tier-multiplier-${tier.id}`} />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">One-shot bonus (₹)</Label>
          <Input type="number" inputMode="numeric" value={tier.reward_value_inr ?? 0} onChange={(e) => set({ reward_value_inr: Number(e.target.value) || 0 })} disabled={locked} className="mt-1 h-8 font-mono-num text-xs" data-testid={`admin-tier-bonus-${tier.id}`} />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Period</Label>
          <Select value={tier.period || 'yearly'} onValueChange={(v) => set({ period: v })} disabled={locked}>
            <SelectTrigger className="mt-1 h-8 text-xs" data-testid={`admin-tier-period-${tier.id}`}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="yearly">Yearly</SelectItem>
              <SelectItem value="anniversary">Anniversary</SelectItem>
              <SelectItem value="lifetime">Lifetime</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="mt-2">
        <ChipList label="Benefits (text)" items={tier.benefits || []} onChange={(v) => set({ benefits: v })} placeholder="Lounge access, concierge, gift voucher…" testid={`admin-tier-benefits-${tier.id}`} locked={locked} />
      </div>
      <div className="mt-2 text-[10px] text-muted-foreground">
        Tiers are <b>highest-only</b>: only the highest crossed threshold applies. The one-shot bonus is credited once when the threshold is first crossed in the period; the rate multiplier applies to primary + spillover earnings while the tier is active.
      </div>
      <Textarea value={tier.notes || ''} onChange={(e) => set({ notes: e.target.value })} rows={2} placeholder="Notes — eligibility quirks, downgrade conditions, observed behaviour…" className="mt-2 text-[11px]" disabled={locked} data-testid={`admin-tier-notes-${tier.id}`} />
    </Card>
  );
};


// ──────────────────────────────────────────────────────────────────────
// Top-level RuleBuilder export. Hosts the four sub-tabs (Rules /
// Milestones / Tiers / Pool Capping) and the rule editor drawer.
// ──────────────────────────────────────────────────────────────────────
export const RuleBuilder = ({ value, onChange, locked, status, cardDefaults }) => {
  const rules = value?.rules || [];
  const milestones = value?.milestones || [];
  const tiers = value?.tiers || [];
  const capPools = value?.cap_pools || [];

  const setRules = (arr) => onChange({ ...value, rules: arr });
  const setMilestones = (arr) => onChange({ ...value, milestones: arr });
  const setTiers = (arr) => onChange({ ...value, tiers: arr });
  const setCapPools = (arr) => onChange({ ...value, cap_pools: arr });

  const updateRule = (id, next) => setRules(rules.map((r) => (r.id === id ? next : r)));
  const removeRule = (id) => setRules(rules.filter((r) => r.id !== id));
  const moveRule = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= rules.length) return;
    const arr = [...rules];
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    setRules(arr);
  };

  // Drawer state — single source of truth for which rule is open
  const [editingRuleId, setEditingRuleId] = useState(null);
  const editingRule = useMemo(() => rules.find((r) => r.id === editingRuleId) || null, [rules, editingRuleId]);

  // Pool usage counts
  const poolUsage = useMemo(() => {
    const map = new Map();
    for (const r of rules) {
      const pid = r?.cap?.pool_id;
      if (pid) map.set(pid, (map.get(pid) || 0) + 1);
    }
    return map;
  }, [rules]);

  const updatePool = (id, next) => setCapPools(capPools.map((p) => (p.id === id ? next : p)));
  const removePool = (id) => {
    setCapPools(capPools.filter((p) => p.id !== id));
    setRules(rules.map((r) => (r?.cap?.pool_id === id ? { ...r, cap: { ...(r.cap || {}), pool_id: null } } : r)));
  };

  const updateTier = (id, next) => setTiers(tiers.map((t) => (t.id === id ? next : t)));
  const removeTier = (id) => setTiers(tiers.filter((t) => t.id !== id));

  const issues = useMemo(() => {
    const out = [];
    for (const r of rules) {
      if (!r.label?.trim()) out.push(`Rule "${r.id}" has no label`);
      if (!r.exclude && !r.earn) out.push(`Rule "${r.label}" has no earn config`);
      if (!r.exclude && r.earn?.type === 'rate_per_spend' && !r.earn?.per) out.push(`Rule "${r.label}": "per" must be > 0`);
      const hasMatch = (r.match?.categories || []).length || (r.match?.mcc || []).length || (r.match?.merchants || []).length || (r.match?.contexts || []).length;
      if (!hasMatch) out.push(`Rule "${r.label}" has no match criteria`);
      const pid = r?.cap?.pool_id;
      if (pid && !capPools.find((p) => p.id === pid)) out.push(`Rule "${r.label}" references missing pool ${pid}`);
    }
    for (const p of capPools) {
      if (!p.label?.trim()) out.push(`Pool "${p.id}" has no label`);
      if (poolLimit(p) == null) out.push(`Pool "${p.label}" has no limit set`);
    }
    for (const t of tiers) {
      if (!t.name?.trim()) out.push(`Tier "${t.id}" has no name`);
      if (!(Number(t.threshold_inr) > 0)) out.push(`Tier "${t.name}" threshold must be > 0`);
    }
    return out;
  }, [rules, capPools, tiers]);

  const handleAddRule = () => {
    const next = emptyRule(cardDefaults);
    setRules([...rules, next]);
    setEditingRuleId(next.id);
  };
  const handleDuplicate = (r, i) => {
    const dup = {
      ...JSON.parse(JSON.stringify(r)),
      id: genRuleId(),
      label: `${r.label || 'Rule'} (copy)`,
      provenance: {
        ...(r.provenance || defaultProvenance()),
        created_by: null, created_by_email: null, created_at: null,
        last_modified_by: null, last_modified_by_email: null, last_modified_at: null,
      },
    };
    const arr = [...rules];
    arr.splice(i + 1, 0, dup);
    setRules(arr);
  };

  return (
    <TooltipProvider delayDuration={300}>
      <div data-testid="admin-rule-builder-pane">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <div className="text-sm font-semibold">Rule builder</div>
            <Badge variant="outline" className="text-[10px]">{status || 'draft'}</Badge>
            {locked && (
              <Tooltip>
                <TooltipTrigger asChild><Badge variant="outline" className="text-[10px] gap-1"><Eye className="h-3 w-3" /> Read-only</Badge></TooltipTrigger>
                <TooltipContent side="bottom">This version is {status}. Create a new draft to edit.</TooltipContent>
              </Tooltip>
            )}
          </div>
          <div className="text-[10px] text-muted-foreground">Rules evaluated top → bottom by priority</div>
        </div>

        <Tabs defaultValue="visual">
          <TabsList className="flex-wrap h-auto">
            <TabsTrigger value="visual" data-testid="admin-rule-builder-tab-visual">Rules ({rules.length})</TabsTrigger>
            <TabsTrigger value="milestones" data-testid="admin-rule-builder-tab-milestones">Milestones ({milestones.length})</TabsTrigger>
            <TabsTrigger value="tiers" data-testid="admin-rule-builder-tab-tiers"><TrendingUp className="h-3 w-3 mr-1" /> Tiers ({tiers.length})</TabsTrigger>
            <TabsTrigger value="pools" data-testid="admin-rule-builder-tab-pools"><Coins className="h-3 w-3 mr-1" /> Pools ({capPools.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="visual" className="mt-3 space-y-2">
            {issues.length > 0 && (
              <Card className="p-2.5 border-amber-500/40 bg-amber-500/5 text-amber-700 dark:text-amber-400 text-[11px] flex gap-2" data-testid="admin-rule-builder-issues">
                <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                <div className="space-y-0.5">
                  <div className="font-medium">{issues.length} validation issue{issues.length === 1 ? '' : 's'}:</div>
                  <ul className="list-disc list-inside">{issues.slice(0, 5).map((i) => <li key={i}>{i}</li>)}</ul>
                </div>
              </Card>
            )}
            {rules.length === 0 ? (
              <Card className="p-8 text-center text-muted-foreground border-dashed" data-testid="admin-rule-builder-empty">
                <Layers className="h-5 w-5 mx-auto opacity-50 mb-2" />
                <div className="text-xs">No rules yet. Add the first rule to start defining how this card earns.</div>
                <Button size="sm" className="mt-3 gap-1" disabled={locked} onClick={handleAddRule} data-testid="admin-rule-builder-add-first">
                  <Plus className="h-3.5 w-3.5" /> Add first rule
                </Button>
              </Card>
            ) : (
              <>
                {rules.map((r, i) => (
                  <RuleListRow
                    key={r.id}
                    rule={r}
                    index={i}
                    total={rules.length}
                    locked={locked}
                    onMove={moveRule}
                    onEdit={() => setEditingRuleId(r.id)}
                    onDuplicate={() => handleDuplicate(r, i)}
                    onRemove={() => removeRule(r.id)}
                    onQuickPatch={(patch) => updateRule(r.id, { ...r, ...patch })}
                  />
                ))}
              </>
            )}
            {rules.length > 0 && (
              <Button size="sm" variant="outline" disabled={locked} onClick={handleAddRule} className="gap-1" data-testid="admin-rule-builder-add">
                <Plus className="h-3.5 w-3.5" /> Add rule
              </Button>
            )}
          </TabsContent>

          <TabsContent value="milestones" className="mt-3 space-y-1.5">
            {milestones.length === 0 ? (
              <Card className="p-8 text-center text-muted-foreground border-dashed">
                <div className="text-xs">No milestones configured.</div>
                <div className="text-[10px] text-muted-foreground mt-1">Milestones are <b>additive</b> — every crossed threshold credits its own bonus.</div>
                <Button size="sm" className="mt-3 gap-1" disabled={locked} onClick={() => setMilestones([emptyMilestone()])} data-testid="admin-milestone-add-first">
                  <Plus className="h-3.5 w-3.5" /> Add milestone
                </Button>
              </Card>
            ) : (
              <>
                <div className="px-1 text-[9px] uppercase tracking-[0.14em] text-muted-foreground">
                  {milestones.length} milestone{milestones.length === 1 ? '' : 's'} · additive
                </div>
                {milestones.map((m) => (
                  <MilestoneBlock
                    key={m.id}
                    m={m}
                    locked={locked}
                    onChange={(next) => setMilestones(milestones.map((x) => (x.id === m.id ? next : x)))}
                    onRemove={() => setMilestones(milestones.filter((x) => x.id !== m.id))}
                  />
                ))}
              </>
            )}
            {milestones.length > 0 && (
              <Button size="sm" variant="outline" disabled={locked} onClick={() => setMilestones([...milestones, emptyMilestone()])} className="gap-1" data-testid="admin-milestone-add">
                <Plus className="h-3.5 w-3.5" /> Add milestone
              </Button>
            )}
          </TabsContent>

          <TabsContent value="tiers" className="mt-3 space-y-2">
            <div className="text-[11px] text-muted-foreground" data-testid="admin-tiers-help">
              Card-level loyalty tiers (e.g. <i>Burgundy Private</i>, <i>Atlas Gold</i>). Only the <b>highest crossed</b> tier
              applies; its <b>rate multiplier</b> scales primary + spillover earnings while it's active, and its <b>one-shot
              bonus</b> credits once on first crossing within the period.
            </div>
            {tiers.length === 0 ? (
              <Card className="p-8 text-center text-muted-foreground border-dashed" data-testid="admin-tiers-empty">
                <TrendingUp className="h-5 w-5 mx-auto opacity-50 mb-2" />
                <div className="text-xs">No tiers configured.</div>
                <Button size="sm" className="mt-3 gap-1" disabled={locked} onClick={() => setTiers([emptyTier()])} data-testid="admin-tier-add-first">
                  <Plus className="h-3.5 w-3.5" /> Add first tier
                </Button>
              </Card>
            ) : (
              <>
                {tiers
                  .slice()
                  .sort((a, b) => (Number(a.threshold_inr) || 0) - (Number(b.threshold_inr) || 0))
                  .map((t) => (
                    <TierBlock
                      key={t.id}
                      tier={t}
                      locked={locked}
                      onChange={(next) => updateTier(t.id, next)}
                      onRemove={() => removeTier(t.id)}
                    />
                  ))}
              </>
            )}
            {tiers.length > 0 && (
              <Button size="sm" variant="outline" disabled={locked} onClick={() => setTiers([...tiers, emptyTier()])} className="gap-1" data-testid="admin-tier-add">
                <Plus className="h-3.5 w-3.5" /> Add tier
              </Button>
            )}
          </TabsContent>

          <TabsContent value="pools" className="mt-3 space-y-2">
            <div className="text-[11px] text-muted-foreground" data-testid="admin-pools-help">
              Shared cap pools let <b>multiple rules drain a single counter</b> (e.g. HSBC TravelOne's
              18,000 RP/month pool shared by Hotels-12X and Flights-6X). Inside each rule's Cap section, pick
              the pool from the <b>Shared pool</b> dropdown — rule-local cap fields will be overridden by the
              pool's config.
            </div>
            {capPools.length === 0 ? (
              <Card className="p-8 text-center text-muted-foreground border-dashed" data-testid="admin-pools-empty">
                <Coins className="h-5 w-5 mx-auto opacity-50 mb-2" />
                <div className="text-xs">No shared cap pools configured.</div>
                <Button size="sm" className="mt-3 gap-1" disabled={locked} onClick={() => setCapPools([emptyCapPool()])} data-testid="admin-cap-pool-add-first">
                  <Plus className="h-3.5 w-3.5" /> Add first pool
                </Button>
              </Card>
            ) : (
              <>
                {capPools.map((p) => (
                  <CapPoolBlock
                    key={p.id}
                    pool={p}
                    locked={locked}
                    usageCount={poolUsage.get(p.id) || 0}
                    allRules={rules}
                    onChange={(next) => updatePool(p.id, next)}
                    onRemove={() => removePool(p.id)}
                  />
                ))}
              </>
            )}
            {capPools.length > 0 && (
              <Button size="sm" variant="outline" disabled={locked} onClick={() => setCapPools([...capPools, emptyCapPool()])} className="gap-1" data-testid="admin-cap-pool-add">
                <Plus className="h-3.5 w-3.5" /> Add pool
              </Button>
            )}
          </TabsContent>
        </Tabs>

        {/* Drawer */}
        <RuleEditorDrawer
          open={!!editingRule}
          onClose={() => setEditingRuleId(null)}
          rule={editingRule}
          onChange={(next) => updateRule(editingRule.id, next)}
          capPools={capPools}
          allRules={rules}
          cardDefaults={cardDefaults}
          locked={locked}
        />
      </div>
    </TooltipProvider>
  );
};
